import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import I18n from "../../i18n"
import { calculateEstimateTotals } from "./estimate_totals.mjs"
import { formatCurrency } from "./currency.mjs"
import { confirmWithDialog } from "./confirm_controller"

// Choice of the recap's global discount that reveals the free percentage field (ERP::SalesHelper::GLOBAL_DISCOUNT_OTHER)
const GLOBAL_DISCOUNT_OTHER = "other"
const t = (key, options) => I18n.t(`erp.sales.js.estimate_editor.${key}`, options)

// Drives the estimate editor: textile lines (with their size breakdown), markings (with the
// textile lines they are attached to), sections, and the live summary panel.
// Connects to data-controller="erp--estimate-editor"
export default class extends Controller {
  static targets = [
    "sectionsContainer", "itemsList", "itemTemplate", "sectionTemplate", "sizeTemplate",
    "allocationItemTemplate", "allocationSizeTemplate",
    "item", "section", "destroyField", "positionField", "sectionIdField", "sectionEditorKeyField",
    "sectionDestroyField", "sectionPositionField", "itemForm", "sizeList", "quantityField",
    "itemSummaryRef", "itemSummaryColor", "itemSummaryDescription", "itemSummarySizes",
    "itemSummaryUnitPrice", "itemSummaryTotal",
    "markingsList", "markingTemplate", "marking", "markingForm",
    "markingDestroyField", "markingPositionField",
    "totalQty", "totalTextiles", "totalItemsSubtotal", "totalSubtotal", "totalGlobalDiscount",
    "totalVat", "totalTtc", "globalDiscountType", "globalDiscountChoice", "globalDiscountPercentage", "globalDiscountAmount",
    "globalDiscountCustomField", "globalDiscountFixedHint", "globalDiscountRow",
    "markingsBreakdown"
  ]

  static values = {
    itemIndex: Number,
    markingIndex: Number,
    sectionIndex: Number
  }

  connect() {
    this._queuedStructuralActions = []
    this._pendingTextileMarkingItems = null
    this._autosaveForm = this.element.closest("form")
    this._onAutosaveRequestFinished = this.#handleAutosaveRequestFinished.bind(this)
    this._autosaveForm?.addEventListener("erp--autosave:request-finished", this._onAutosaveRequestFinished)
    this._initSortable()
    this.#normalizeOpenMarkings()
    this._recalculateTotals()
  }

  disconnect() {
    this._autosaveForm?.removeEventListener("erp--autosave:request-finished", this._onAutosaveRequestFinished)
  }

  // ── Textile lines ────────────────────────────────────────────

  // The picker is a single modal shared by every "add a textile" button, so the button that
  // opened it decides which section the new lines land in.
  openPicker(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "openPicker", false)) return

    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    this._pendingReplacement = item || null
    this._pendingContainer = item?.closest("[data-erp--estimate-editor-target='itemsList']")
      || event.target.closest("[data-erp--estimate-editor-target='section']")
        ?.querySelector("[data-erp--estimate-editor-target='itemsList']")
      || event.target.closest("[data-erp--estimate-editor-target='itemsList']")
      || this._ungroupedItemsList()

    document.dispatchEvent(new CustomEvent("textile-picker:open", { detail: this.#pickerDetail(item, event.params?.tab) }))
  }

  // A replaced line reopens on its own origin; an add button names the tab it opens
  #pickerDetail(item, tab) {
    if (!item) return { mode: "add", tab, hasAllocations: false }

    return {
      mode: "replace",
      tab: item.dataset.customerSupplied === "true" ? "customer" : "catalogue",
      hasAllocations: this.#itemHasAllocations(item)
    }
  }

  async replaceItem(event) {
    event.stopPropagation()
    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (item && this.#itemHasAllocations(item)) {
      const confirmed = await confirmWithDialog(t("replace_allocated_title"), null, {
        dataset: { erpConfirmText: t("replace_allocated_confirm"), erpConfirmAccept: t("replace_allocated_accept") }
      })
      if (!confirmed) return
    }

    this.openPicker(event)
  }

  // Called by the erp--textile-picker:items-selected event. One line per colourway, with
  // its sizes broken down inside — never one line per size.
  addSelectedItems(event) {
    if (this.#deferStructuralAction(event, "addSelectedItems")) return
    const { colours } = event.detail
    if (!colours?.length) return

    const container = this._pickerContainer()
    if (!container) return

    const replacement = this.#consumeReplacement()
    if (replacement) {
      const replacementMoves = this.#planReplacementMoves(replacement, colours)
      const preservedVariantIds = new Set(
        [...replacementMoves.values()].flat().map(move => move.sourceId)
      )
      this.#replaceCatalogueItem(replacement, colours[0], preservedVariantIds)
      colours.slice(1).forEach((colour, offset) => {
        const newItem = this.#appendItem(container)
        this.#populateCatalogueItem(newItem, colour)
        this.#bindReplacementMoves(newItem, replacementMoves.get(offset + 1) || [])
      })
      this._updatePositions(container)
      this._initSortableForList(container)
      this.syncAllocations()
      this._recalculateTotals()
      this.dispatch("changed")
      this._triggerAutosave()
      return
    }

    const newItems = colours.map(colour => {
      const newItem = this.#appendItem(container)
      this.#populateCatalogueItem(newItem, colour)
      return newItem
    })

    this._updatePositions(container)
    this._initSortableForList(container)
    this.syncAllocations()
    this._recalculateTotals()
    this.dispatch("changed")
    this.#scheduleTextileMarkingFlow(newItems)
  }

  // Called by the erp--textile-picker:custom-selected event: a garment the customer brings in,
  // so the line carries a free description instead of a catalogue reference.
  addCustomItem(event) {
    if (this.#deferStructuralAction(event, "addCustomItem")) return
    const { garment_type, garment_label, color_name, comment, sizes, quantity, description, unit_price_cents } = event.detail
    if (!sizes?.length && !quantity) return

    const container = this._pickerContainer()
    if (!container) return

    const replacement = this.#consumeReplacement()
    if (replacement) {
      this.#replaceCustomItem(replacement, event.detail)
      this.syncAllocations()
      this._recalculateTotals()
      this.dispatch("changed")
      this._triggerAutosave()
      return
    }

    const newItem = this.#appendItem(container)
    this.#populateCustomItem(newItem, {
      garment_type, garment_label, color_name, comment, sizes, quantity, description, unit_price_cents
    })

    this._updatePositions(container)
    this._initSortableForList(container)
    this.syncAllocations()
    this._recalculateTotals()
    this.dispatch("changed")
    this.#scheduleTextileMarkingFlow([ newItem ])
  }

  cancelPicker() {
    this._pendingReplacement = null
    this._pendingContainer = null
  }

  // Called by erp--customer-combobox:selected. The combobox fills the addresses; here the lines learn the new
  // customer type, which their pricing depends on.
  customerChanged(event) {
    const customerType = event.detail?.customerType
    if (!customerType) return

    this.#visible(this.itemTargets).forEach(item => {
      const manual = item.querySelector("input[type='checkbox'][name*='[unit_price_override]']")?.checked
      const complimentary = item.querySelector("input[type='checkbox'][name*='[complimentary]']")?.checked
      const marginOverride = item.querySelector("input[name*='[textile_margin_rate]']")?.value
      if (item.dataset.customerSupplied === "true" || manual || complimentary || marginOverride) return

      item.dataset.pricingStale = "true"
      this.#refreshItemSummary(item)
    })

    this.markingTargets.forEach(marking => {
      marking.setAttribute("data-erp--marking-form-customer-type-value", customerType)
      this.application
        .getControllerForElementAndIdentifier(marking, "erp--marking-form")
        ?.customerContextChanged(customerType)
    })
    this._recalculateTotals()
  }

  // The server returns the persisted source→target ids after moving allocations. Rebuild the
  // open allocation panels in place so their labels and hidden ids immediately reflect the new
  // price line without requiring a page reload.
  reconcileVariantReplacements(event) {
    const replacements = event.detail?.replacements || {}
    if (Object.keys(replacements).length === 0) return

    Object.entries(replacements).forEach(([sourceId, targetId]) => {
      const sourceRow = this.#sizeRowById(sourceId)
      const targetRow = this.#sizeRowById(targetId)
      const targetItem = targetRow?.closest("[data-erp--estimate-editor-target='item']")
      if (!targetRow || !targetItem) return

      this.markingTargets.forEach(marking => {
        const allocationRows = [...marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']")]
          .filter(row => row.querySelector("input[name$='[estimate_item_variant_id]']")?.value === sourceId)
        if (allocationRows.length === 0) return

        const targetSection = this.#ensureAllocationItemSection(marking, targetItem)
        const targetSizes = targetSection?.querySelector("[data-allocation-sizes]")
        if (!targetSizes) return

        allocationRows.forEach(row => {
          row.querySelector("input[name$='[estimate_item_variant_id]']").value = targetId
          this.#refreshMovedAllocationRow(row, targetItem, targetRow)
          targetSizes.appendChild(row)
        })

        marking.querySelectorAll("[data-allocation-item]").forEach(section => {
          if (section !== targetSection && !section.querySelector("[data-erp--marking-form-target='allocationRow']")) {
            section.remove()
          }
        })
      })

      sourceRow?.remove()
      const replacementField = targetRow.querySelector("[data-replacement-source-variant-id]")
      if (replacementField) replacementField.value = ""
    })

    this.itemTargets.forEach(item => {
      const variantIds = new Set(
        [...item.querySelectorAll("[data-size-row] input[type='hidden'][name$='[id]']")]
          .map(input => input.value)
          .filter(Boolean)
      )
      item.dataset.hasMarkingAllocations = String(
        [...this.element.querySelectorAll("[data-erp--marking-form-target='allocationRow']")].some(row => {
          const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
          const destroyed = row.querySelector("input[name$='[_destroy]']")?.value === "1"
          return variantIds.has(variantId) && !destroyed
        })
      )
    })
  }

  // The delete button of a textile card asks through the ERP confirmation dialog first
  async confirmRemoveItem(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    if (!(await confirmWithDialog(button.dataset.confirmTitle, null, button))) return

    this.removeItem({ preventDefault() {}, stopPropagation() {}, target: button, currentTarget: button, params: {} })
  }

  // A copy of the line right after it: same product or garment, prices and sizes; files and markings stay behind
  duplicateItem(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "duplicateItem")) return

    const source = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!source) return

    const container = source.parentElement
    const copy = this.#appendItem(container)
    container.insertBefore(copy, source.nextElementSibling)
    this.#copyItemFields(source, copy)
    // Lets the server copy the costs a commercial user cannot see (EstimateItem#inherit_costs_from_duplicated_item)
    const sourceField = copy.querySelector("[data-duplicated-from-field]")
    if (sourceField) sourceField.value = source.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value || ""

    this._updatePositions(container)
    this.syncAllocations()
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
    copy.querySelector("[data-item-toggle]")?.focus()
  }

  removeItem(event) {
    event.preventDefault()
    event.stopPropagation()

    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!item) return
    if (this.#deferStructuralAction(event, "removeItem")) {
      this.#cancelPendingTextileMarkingItem(item)
      return
    }

    const automaticMarkings = this.#automaticMarkingsAllocatedToItem(item)
    this.#discard(item, "destroyField")
    this.syncAllocations()
    this.#discardEmptyAutomaticMarkings(automaticMarkings)
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
  }

  moveItem(event) {
    if (this.#deferStructuralAction(event, "moveItem")) return
    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!item || !this.#moveSibling(item, event.params.direction, "[data-erp--estimate-editor-target='item']")) return

    this._updatePositions(item.parentElement)
    this.dispatch("changed")
    this._triggerAutosave()
    event.currentTarget.focus()
  }

  moveItemSection(event) {
    if (this.#deferStructuralAction(event, "moveItemSection")) return
    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!item) return

    const lists = this.itemsListTargets.filter(list => list.closest("[data-erp--estimate-editor-target='section']")?.style.display !== "none")
    const currentList = item.parentElement
    const currentIndex = lists.indexOf(currentList)
    const destination = lists[currentIndex + (event.params.direction === "previous" ? -1 : 1)]
    if (!destination) return

    destination.appendChild(item)
    this._updatePositions(currentList)
    this._updatePositions(destination)
    this._updateSectionIdForItem(item, destination)
    this.dispatch("changed")
    this._triggerAutosave()
    event.currentTarget.focus()
  }

  toggleItem(event) {
    event.preventDefault()
    event.stopPropagation()

    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    const form = item?.querySelector("[data-erp--estimate-editor-target='itemForm']")
    if (!form) return

    form.classList.toggle("hidden")
    item.querySelector("[data-item-toggle]")?.setAttribute("aria-expanded", String(!form.classList.contains("hidden")))
  }

  updateItemSummary(event) {
    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    if (!item) return

    this.#syncQuantity(item)
    this.#refreshItemSummary(item)
    this.syncAllocations()
    this._recalculateTotals()
  }

  // Persisted textile/size ids arrive after autosave. Resynchronizing every active marking
  // also keeps limits and labels correct when a textile quantity changes or a line disappears.
  syncAllocations() {
    this.#visible(this.markingTargets).forEach(marking => this.#syncMarkingAllocationRows(marking))
  }

  // ── Markings ─────────────────────────────────────────────────

  // Opens the 4-step wizard on a new marking, every textile size selected (`.alloc` of the mockup: all checked).
  // "+ Marquage" on a textile card selects the sizes of that line only.
  addMarking(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "addMarking", false)) return
    if (!this.hasMarkingsListTarget) return

    const item = event.target.closest("[data-erp--estimate-editor-target='item']")
    this.#closeOtherMarkings()
    const marking = this.#appendMarking()
    this.#syncMarkingAllocationRows(marking)
    this.#selectMarkingAllocations(marking, item && this.#savedVariantIds(item))
    this._updateMarkingPositions()
    this._recalculateTotals()
    this.dispatch("changed")
    this.#openMarkingWizard(marking)
  }

  // Copies a marking's configuration into a new marking, leaving its textile attachments empty
  // so the user picks which other products it applies to in its wizard.
  duplicateMarking(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.#deferStructuralAction(event, "duplicateMarking", false)) return

    const source = event.target.closest("[data-erp--estimate-editor-target='marking']")
    if (!source) return

    this.#closeOtherMarkings()
    const marking = this.#appendMarking()
    source.after(marking)
    this.#syncMarkingAllocationRows(marking)
    this.#copyMarkingFields(source, marking)

    this._updateMarkingPositions()
    this._recalculateTotals()
    this.dispatch("changed")
    this.#openMarkingWizard(marking)
  }

  #copyMarkingFields(source, marking) {
    const copied = ["technique", "sub_technique", "custom_marking_zone_name", "marking_position", "width_mm", "height_mm",
      "dimension_1", "dimension_2", "unit_price", "vat_rate", "discount_type",
      "discount_percentage", "manual_price_reason", "notes"]

    copied.forEach(name => {
      const from = source.querySelector(`[name*='[${name}]']`)
      if (from) this.#setField(marking, name, from.value)
    })
    ;["marking_technique_id", "marking_zone_id"].forEach(name => this.#copyMarkingChoice(source, marking, name))
    this.#copyPersonalizedPieces(source, marking)
    const nextArtworkVersion = Math.max(0, ...this.markingTargets.map(row => {
      return parseInt(row.querySelector("input[name*='[artwork_version]']")?.value) || 0
    })) + 1
    this.#setField(marking, "artwork_version", String(nextArtworkVersion))

    ;["unit_price_override", "artwork_pending_from_customer"].forEach(name => {
      const from = source.querySelector(`input[type='checkbox'][name*='[${name}]']`)
      const to = marking.querySelector(`input[type='checkbox'][name*='[${name}]']`)
      if (!from || !to) return

      to.checked = from.checked
      to.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  // The count asked for: erp--marking-form caps it to the pieces of the copy once its textiles are chosen
  #copyPersonalizedPieces(source, marking) {
    const from = source.querySelector("input[name*='[personalized_pieces_count]']")
    const to = marking.querySelector("input[name*='[personalized_pieces_count]']")
    if (!from || !to) return

    to.value = from.value
    to.dataset.requested = from.dataset.requested ?? from.value
  }

  // Technique and zone are radio groups: check the same choice in the copy
  #copyMarkingChoice(source, marking, name) {
    const value = source.querySelector(`input[type='radio'][name$='[${name}]']:checked`)?.value
    const choice = [...marking.querySelectorAll(`input[type='radio'][name$='[${name}]']`)].find(input => input.value === value)
    if (choice) choice.checked = true
  }

  // A new marking whose wizard closed before "Ajouter le marquage" (erp--marking-wizard:discarded)
  discardMarking(event) {
    if (this.#deferStructuralAction(event, "discardMarking")) return

    const marking = event.target.closest("[data-erp--estimate-editor-target='marking']")
    if (!marking) return

    const saved = Boolean(marking.querySelector("input[name*='[id]']")?.value)
    this.#discard(marking, "markingDestroyField")
    this._updateMarkingPositions()
    this._recalculateTotals()
    this.dispatch("changed")
    if (saved) this._triggerAutosave()
  }

  // The delete button of a marking card asks through the ERP confirmation dialog first
  async confirmRemoveMarking(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    if (!(await confirmWithDialog(button.dataset.confirmTitle, null, button))) return

    this.removeMarking({ preventDefault() {}, stopPropagation() {}, target: button, currentTarget: button, params: {} })
  }

  removeMarking(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.#deferStructuralAction(event, "removeMarking")) return

    const marking = event.target.closest("[data-erp--estimate-editor-target='marking']")
    if (!marking) return

    this.#discard(marking, "markingDestroyField")
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
  }

  moveMarking(event) {
    if (this.#deferStructuralAction(event, "moveMarking")) return
    const marking = event.target.closest("[data-erp--estimate-editor-target='marking']")
    if (!marking || !this.#moveSibling(marking, event.params.direction, "[data-erp--estimate-editor-target='marking']")) return

    this._updateMarkingPositions()
    this.dispatch("changed")
    this._triggerAutosave()
    event.currentTarget.focus()
  }

  // The "Tarif" button of a marking card opens its pricing panel, one at a time
  toggleMarking(event) {
    event.preventDefault()
    event.stopPropagation()

    const marking = event.target.closest("[data-erp--estimate-editor-target='marking']")
    const form = marking?.querySelector("[data-erp--estimate-editor-target='markingForm']")
    const willOpen = form?.classList.contains("hidden")
    this.#closeOtherMarkings(marking)
    if (marking) {
      if (willOpen) this.#syncMarkingAllocationRows(marking)
      this.#setMarkingOpen(marking, willOpen)
    }
  }

  #closeOtherMarkings(except = null) {
    this.markingTargets.forEach(marking => {
      if (marking === except) return
      this.#setMarkingOpen(marking, false)
    })
  }

  #normalizeOpenMarkings() {
    let openMarkingFound = false

    this.markingTargets.forEach(marking => {
      const form = marking.querySelector("[data-erp--estimate-editor-target='markingForm']")
      const isOpen = form && !form.classList.contains("hidden")
      const keepOpen = isOpen && !openMarkingFound
      if (keepOpen) openMarkingFound = true
      this.#setMarkingOpen(marking, keepOpen)
    })
  }

  #setMarkingOpen(marking, open) {
    marking.querySelector("[data-erp--estimate-editor-target='markingForm']")?.classList.toggle("hidden", !open)
    marking.querySelector("[data-marking-toggle]")?.setAttribute("aria-expanded", String(open))
  }

  #syncMarkingAllocationRows(marking) {
    if (!this.hasAllocationItemTemplateTarget || !this.hasAllocationSizeTemplateTarget) return

    const list = marking.querySelector("[data-marking-allocation-list]")
    const positionField = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")
    if (!list || !positionField?.name) return

    const markingPrefix = positionField.name.replace(/\[position\]$/, "")
    const existingRows = new Map(
      [...marking.querySelectorAll("input[name*='[allocations_attributes]'][name$='[estimate_item_variant_id]']")]
        .filter(input => input.value)
        .map(input => [input.value, input.closest("[data-erp--marking-form-target='allocationRow']")])
    )
    const visibleVariantIds = new Set()
    let automaticAllocationRemoved = false
    let allocationIndex = [...marking.querySelectorAll("input[name*='[allocations_attributes]']")]
      .map(input => input.name.match(/\[allocations_attributes\]\[(\d+)\]/)?.[1])
      .filter(index => index != null)
      .reduce((maximum, index) => Math.max(maximum, parseInt(index)), -1) + 1

    this.#visible(this.itemTargets).forEach(item => {
      const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
      if (!itemId) return

      const persistedSizes = [...item.querySelectorAll("[data-size-row]")].filter(sizeRow => {
        const persisted = Boolean(sizeRow.querySelector("input[type='hidden'][name$='[id]']")?.value)
        const destroyed = sizeRow.querySelector("input[name$='[_destroy]']")?.value === "1"
        return persisted && !destroyed
      })
      if (persistedSizes.length === 0) return

      let itemSection = [...list.querySelectorAll("[data-allocation-item]")]
        .find(section => section.dataset.allocationItemId === itemId)
      if (!itemSection) {
        list.insertAdjacentHTML(
          "beforeend",
          this.allocationItemTemplateTarget.innerHTML.replaceAll("ITEM_ID", itemId)
        )
        itemSection = list.lastElementChild
      }
      this.#fillAllocationItemSummary(itemSection, item)

      const sizesContainer = itemSection.querySelector("[data-allocation-sizes]")
      if (!sizesContainer) return

      persistedSizes.forEach(sizeRow => {
        const variantId = sizeRow.querySelector("input[type='hidden'][name$='[id]']")?.value
        if (!variantId) return

        const availableQuantity = parseInt(sizeRow.querySelector("input[name$='[quantity]']")?.value) || 0
        const textileType = item.dataset.textileType === "white" ? "white" : "colored"
        const sizeLabel = sizeRow.querySelector("[data-size-label]")?.textContent?.trim() || ""
        const itemLabel = item.querySelector("[data-erp--estimate-editor-target='itemSummaryRef']")?.textContent?.trim()
          || item.querySelector("[data-erp--estimate-editor-target='itemSummaryDescription']")?.textContent?.trim()
          || ""
        const existingRow = existingRows.get(variantId)
        visibleVariantIds.add(variantId)

        if (existingRow) {
          if (this.#refreshAllocationRow(existingRow, {
            availableQuantity, itemLabel, sizeLabel, textileType, list
          })) automaticAllocationRemoved = true
          return
        }

        const html = this.allocationSizeTemplateTarget.innerHTML
          .replaceAll("MARKING_PREFIX", markingPrefix)
          .replaceAll("ALLOCATION_INDEX", allocationIndex++)
          .replaceAll("VARIANT_ID", variantId)
          .replaceAll("TEXTILE_TYPE", textileType)
          .replaceAll("AVAILABLE_QUANTITY", availableQuantity)
        sizesContainer.insertAdjacentHTML("beforeend", html)

        const allocationRow = sizesContainer.lastElementChild
        this.#refreshAllocationRow(allocationRow, {
          availableQuantity, itemLabel, sizeLabel, textileType, list
        })
      })
    })

    if (this.#releaseVanishedAllocationRows(existingRows, visibleVariantIds)) automaticAllocationRemoved = true
    this.#hideEmptyAllocationItems(list)
    if (visibleVariantIds.size > 0) list.querySelector("[data-allocation-empty]")?.remove()
    const automaticLinkRemoved = this.#syncAutomaticMarkingItemLinks(marking)
    const markingForm = this.application.getControllerForElementAndIdentifier(marking, "erp--marking-form")
    markingForm?.compatibilityChanged()
    markingForm?.allocationsResynced()

    if ((automaticAllocationRemoved || automaticLinkRemoved) && marking.dataset.automaticTextileMarking === "true") {
      this.#discardEmptyAutomaticMarkings([ marking ])
    }
  }

  // Sizes no longer on the estimate leave the marking; true when one of them carried pieces
  #releaseVanishedAllocationRows(existingRows, visibleVariantIds) {
    let released = false
    existingRows.forEach((row, variantId) => {
      if (!row || visibleVariantIds.has(variantId)) return

      const destroy = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")
      const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
      // A replaced size may already be flagged for destruction (#destroyAllocationsForVariantIds): its pieces tell
      const wasSelected = (parseInt(quantity?.value) || 0) > 0
      if (destroy) destroy.value = "1"
      if (quantity) {
        quantity.value = "0"
        quantity.disabled = true
      }
      row.classList.add("hidden")
      if (!wasSelected) return

      released = true
      destroy?.dispatchEvent(new Event("change", { bubbles: true }))
    })
    return released
  }

  // A textile line left without a size it can mark disappears from the wizard
  #hideEmptyAllocationItems(list) {
    list.querySelectorAll("[data-allocation-item]").forEach(section => {
      const rows = section.querySelectorAll("[data-erp--marking-form-target='allocationRow']")
      const visibleRows = section.querySelectorAll("[data-erp--marking-form-target='allocationRow']:not(.hidden)")
      section.classList.toggle("hidden", rows.length > 0 && visibleRows.length === 0)
    })
  }

  #refreshAllocationRow(row, { availableQuantity, itemLabel, sizeLabel, textileType, list }) {
    row.classList.remove("hidden")
    row.dataset.allocationType = textileType
    row.querySelector("input[name$='[textile_type]']")?.setAttribute("value", textileType)
    row.querySelector("[data-allocation-size]").textContent = sizeLabel
    row.querySelector("[data-allocation-label]").textContent = `${itemLabel} · ${sizeLabel}`
    row.querySelector("[data-allocation-available]").textContent = this.#availableLabel(list, availableQuantity)

    const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
    if (!quantity) return false

    quantity.disabled = false
    quantity.max = String(availableQuantity)
    quantity.setAttribute("aria-label", t("allocation_label", { textile: itemLabel, size: sizeLabel, max: availableQuantity }))
    const currentQuantity = parseInt(quantity.value) || 0
    if (currentQuantity <= availableQuantity) return false

    // erp--marking-form drops the size from the marking when no piece is left
    quantity.value = String(availableQuantity)
    quantity.dispatchEvent(new Event("input", { bubbles: true }))
    return availableQuantity === 0
  }

  #fillAllocationItemSummary(section, item) {
    const reference = item.querySelector("[data-erp--estimate-editor-target='itemSummaryRef']")?.textContent?.trim()
    const description = item.querySelector("[data-erp--estimate-editor-target='itemSummaryDescription']")?.textContent?.trim()
    const color = item.querySelector("[data-erp--estimate-editor-target='itemSummaryColor']")?.textContent?.trim()
    const title = reference || description || ""

    section.querySelector("[data-allocation-item-title]").textContent = title
    section.querySelector("[data-allocation-item-description]").textContent = [ color, description ].filter(Boolean).join(" · ")
    section.querySelector("[data-erp--marking-form-target='allocationItemCheckbox']")
      ?.setAttribute("aria-label", t("allocation_item_label", { textile: title }))
    const compatibility = section.querySelector("[data-erp--marking-form-target='compatibilityRow']")
    if (compatibility) {
      compatibility.dataset.customerSupplied = item.dataset.customerSupplied || "false"
      compatibility.dataset.compatibilityRules = item.dataset.compatibilityRules || "{}"
    }
  }

  #availableLabel(list, quantity) {
    return (list.dataset.availableTemplate || "__COUNT__").replace("__COUNT__", quantity)
  }

  #compatibilityRules(summary = {}) {
    return Object.fromEntries((summary.rules || []).map(rule => [
      `${rule.technique_id}:${rule.zone_id}`,
      {
        status: rule.status,
        max_width_mm: rule.max_width_mm,
        max_height_mm: rule.max_height_mm,
        operator_note: rule.operator_note,
        inherited: rule.inherited
      }
    ]))
  }

  // ── Sections ─────────────────────────────────────────────────

  addSection(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "addSection")) return

    const index = this.sectionIndexValue++
    const editorKey = `section-${crypto.randomUUID()}`
    this.sectionsContainerTarget.insertAdjacentHTML(
      "beforeend",
      this.sectionTemplateTarget.innerHTML
        .replace(/SECTION_INDEX/g, index)
        .replace(/SECTION_EDITOR_KEY/g, editorKey)
    )

    const newSection = this.sectionsContainerTarget.lastElementChild
    const itemsList = newSection.querySelector("[data-erp--estimate-editor-target='itemsList']")
    if (itemsList) this._initSortableForList(itemsList)

    this._updateSectionPositions()
    newSection.querySelector("input[type='text']")?.focus()
    this.dispatch("changed")
    this._triggerAutosave()
  }

  removeSection(event) {
    event.preventDefault()
    if (this.#deferStructuralAction(event, "removeSection")) return

    const section = event.target.closest("[data-erp--estimate-editor-target='section']")
    if (!section) return

    const itemsList = section.querySelector("[data-erp--estimate-editor-target='itemsList']")
    const ungroupedList = this._ungroupedItemsList()

    if (itemsList && ungroupedList) {
      ;[...itemsList.children].filter(el => el.hasAttribute("data-item-index")).forEach(item => {
        const sectionIdField = item.querySelector("[data-erp--estimate-editor-target='sectionIdField']")
        if (sectionIdField) sectionIdField.value = ""
        const sectionEditorKeyField = item.querySelector("[data-erp--estimate-editor-target='sectionEditorKeyField']")
        if (sectionEditorKeyField) sectionEditorKeyField.value = ""
        ungroupedList.appendChild(item)
      })
      this._updatePositions(ungroupedList)
    }

    this.#discard(section, "sectionDestroyField")
    this.dispatch("changed")
    this._triggerAutosave()
  }

  moveSection(event) {
    if (this.#deferStructuralAction(event, "moveSection")) return
    const section = event.target.closest("[data-erp--estimate-editor-target='section']")
    if (!section || !this.#moveSibling(section, event.params.direction, "[data-erp--estimate-editor-target='section']")) return

    this._updateSectionPositions()
    this.dispatch("changed")
    this._triggerAutosave()
    event.currentTarget.focus()
  }

  // ── Totals ───────────────────────────────────────────────────

  recalculate() {
    this._recalculateTotals()
  }

  // Recap discount: none or a standard rate fills the percentage field; "other" reveals it for a rate of its own
  globalDiscountChoiceChanged() {
    const choice = this.globalDiscountChoiceTarget.value
    const other = choice === GLOBAL_DISCOUNT_OTHER
    this.globalDiscountCustomFieldTarget.classList.toggle("hidden", !other)
    if (other) {
      this.globalDiscountPercentageTarget.focus()
      return
    }

    this.globalDiscountPercentageTarget.value = choice
    this.#applyPercentageDiscount()
    // The choice select has no name: the autosave only hears about the fields it filled through this event
    this._triggerAutosave()
  }

  customDiscountChanged() {
    this.#applyPercentageDiscount()
  }

  _recalculateTotals() {
    let textilesQty = 0
    let textilesSubtotalCents = 0
    const taxableLines = []

    this.#visible(this.itemTargets).forEach(item => {
      const qty = this.#lineQuantity(item)
      const totalCents = this.#discountedLineTotalCents(item)

      textilesQty += qty
      textilesSubtotalCents += totalCents
      taxableLines.push({ totalCents, vatRate: this.#vatRate(item) })
    })
    const textilePricingUnavailable = this.#visible(this.itemTargets).some(item => item.dataset.pricingUnavailable === "true")
    const textilePricingStale = this.#visible(this.itemTargets).some(item => item.dataset.pricingStale === "true")

    const visibleMarkings = this.#visible(this.markingTargets)
    const structuredPricingStale = visibleMarkings.some(marking =>
      marking.dataset.pricingStale === "true" || marking.dataset.pricingUnavailable === "true")
    const markingGroups = new Map()

    visibleMarkings.forEach(marking => {
      const quantity = this.#lineQuantity(marking)
      const technique = marking.querySelector("input[name*='[technique]']")?.value || "__untyped__"
      const label = marking.querySelector("[data-erp--marking-form-target='techniqueBadge']")?.textContent?.trim() || "Marquage"
      const stale = marking.dataset.pricingStale === "true" || marking.dataset.pricingUnavailable === "true"
      const totalCents = stale ? null : this.#markingTotalCents(marking)
      const group = markingGroups.get(technique) || { label, quantity: 0, totalCents: 0, stale: false, unavailable: false }
      group.label = label
      group.quantity += quantity
      group.stale ||= stale
      group.unavailable ||= marking.dataset.pricingUnavailable === "true"
      if (totalCents != null) group.totalCents += totalCents
      markingGroups.set(technique, group)

      if (totalCents != null) {
        taxableLines.push({ totalCents, vatRate: this.#vatRate(marking) })
      }
    })
    this.#refreshMarkingsBreakdown([...markingGroups.values()])

    if (structuredPricingStale || textilePricingUnavailable || textilePricingStale) {
      const pending = textilePricingUnavailable || visibleMarkings.some(marking => marking.dataset.pricingUnavailable === "true")
        ? t("pricing_unavailable") : t("reprice_required")
      if (this.hasTotalQtyTarget) this.totalQtyTarget.textContent = t("pieces", { count: textilesQty })
      if (this.hasTotalTextilesTarget) this.totalTextilesTarget.textContent = textilePricingUnavailable || textilePricingStale ? pending : this.#currency(textilesSubtotalCents / 100)
      if (this.hasTotalItemsSubtotalTarget) this.totalItemsSubtotalTarget.textContent = pending
      if (this.hasTotalSubtotalTarget) this.totalSubtotalTarget.textContent = pending
      if (this.hasTotalVatTarget) this.totalVatTarget.textContent = pending
      if (this.hasTotalTtcTarget) this.totalTtcTarget.textContent = pending
      if (this.hasGlobalDiscountRowTarget) this.globalDiscountRowTarget.classList.add("hidden")
      return
    }

    const globalType = this.hasGlobalDiscountTypeTarget ? this.globalDiscountTypeTarget.value : "none"
    const globalPct = this.hasGlobalDiscountPercentageTarget
      ? (parseFloat(this.globalDiscountPercentageTarget.value) || 0)
      : 0
    const fixedDiscountCents = this.hasGlobalDiscountAmountTarget
      ? Math.round((parseFloat(this.globalDiscountAmountTarget.value) || 0) * 100)
      : 0
    const { itemsSubtotalCents, globalDiscountCents, subtotalCents, vatCents, totalCents } =
      calculateEstimateTotals(taxableLines, {
        discountType: globalType,
        discountPercentage: globalPct,
        discountAmountCents: fixedDiscountCents
      })

    if (this.hasTotalQtyTarget) this.totalQtyTarget.textContent = t("pieces", { count: textilesQty })
    if (this.hasTotalTextilesTarget) this.totalTextilesTarget.textContent = this.#currency(textilesSubtotalCents / 100)
    if (this.hasTotalItemsSubtotalTarget) this.totalItemsSubtotalTarget.textContent = this.#currency(itemsSubtotalCents / 100)
    if (this.hasTotalSubtotalTarget) this.totalSubtotalTarget.textContent = this.#currency(subtotalCents / 100)
    if (this.hasTotalVatTarget) this.totalVatTarget.textContent = this.#currency(vatCents / 100)
    if (this.hasTotalTtcTarget) this.totalTtcTarget.textContent = this.#currency(totalCents / 100)

    if (this.hasGlobalDiscountRowTarget) {
      this.globalDiscountRowTarget.classList.toggle("hidden", globalDiscountCents <= 0)
      if (this.hasTotalGlobalDiscountTarget) {
        this.totalGlobalDiscountTarget.textContent = `- ${this.#currency(globalDiscountCents / 100)}`
      }
    }
  }

  // ── Private ──────────────────────────────────────────────────

  // A percentage, even zero, replaces a fixed amount kept from an older quote
  #applyPercentageDiscount() {
    const percentage = parseFloat(this.globalDiscountPercentageTarget.value) || 0
    this.globalDiscountTypeTarget.value = percentage > 0 ? "percentage" : "none"
    this.globalDiscountAmountTarget.value = "0"
    this.globalDiscountFixedHintTargets.forEach(hint => hint.remove())
    this._recalculateTotals()
  }

  #markingTotalCents(marking) {
    // Grid supplements (screen setup, special ink…) have no form field but count in the total.
    if (marking.querySelector("input[type='checkbox'][name*='[complimentary]']")?.checked) return 0

    const structuredPricing = marking.getAttribute("data-erp--marking-form-structured-pricing-value") === "true"
    const overridden = marking.querySelector("input[type='checkbox'][name*='[unit_price_override]']")?.checked
    const serverTotalCents = parseInt(marking.dataset.markingTotalCents)
    if (structuredPricing && !overridden && Number.isFinite(serverTotalCents)) {
      // A mixed white/colour series cannot be reconstructed from its rounded average unit price.
      return serverTotalCents
    }

    const extrasCents = parseInt(marking.dataset.markingExtrasCents) || 0
    return this.#discountedLineTotalCents(marking) + extrasCents
  }

  #refreshMarkingsBreakdown(entries) {
    if (!this.hasMarkingsBreakdownTarget) return

    this.markingsBreakdownTarget.replaceChildren()
    entries.forEach(entry => {
      const row = document.createElement("div")
      row.className = "flex justify-between gap-3"
      const label = document.createElement("span")
      label.textContent = `${entry.label} · ${t("pieces", { count: entry.quantity })}`
      const amount = document.createElement("span")
      amount.className = "font-semibold whitespace-nowrap tabular-nums"
      amount.textContent = entry.unavailable ? t("pricing_unavailable") :
        (entry.stale ? t("reprice_required") : this.#currency(entry.totalCents / 100))
      row.append(label, amount)
      this.markingsBreakdownTarget.appendChild(row)
    })

    if (entries.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-[#B9B4A7]"
      empty.textContent = I18n.t("erp.sales.estimates.form.no_markings")
      this.markingsBreakdownTarget.appendChild(empty)
    }
  }

  #appendItem(container) {
    const index = this.itemIndexValue++
    container.insertAdjacentHTML("beforeend", this.itemTemplateTarget.innerHTML.replace(/ITEM_INDEX/g, index))

    const newItem = container.lastElementChild
    newItem.dataset.itemIndex = index

    const sectionIdField = newItem.querySelector("[data-erp--estimate-editor-target='sectionIdField']")
    if (sectionIdField) sectionIdField.value = container.dataset.sectionId || ""
    const sectionEditorKeyField = newItem.querySelector("[data-erp--estimate-editor-target='sectionEditorKeyField']")
    if (sectionEditorKeyField) sectionEditorKeyField.value = container.dataset.sectionEditorKey || ""

    return newItem
  }

  #appendMarking() {
    const index = this.markingIndexValue++
    this.markingsListTarget.insertAdjacentHTML(
      "beforeend",
      this.markingTemplateTarget.innerHTML.replace(/MARKING_INDEX/g, index)
    )

    const marking = this.markingsListTarget.lastElementChild
    marking.dataset.markingIndex = index
    const customerType = this.#currentCustomerType()
    marking.setAttribute("data-erp--marking-form-customer-type-value", customerType)
    requestAnimationFrame(() => {
      if (!marking.isConnected) return

      this.application
        .getControllerForElementAndIdentifier(marking, "erp--marking-form")
        ?.customerContextChanged(customerType)
    })
    return marking
  }

  #currentCustomerType() {
    return this.element.querySelector("input[name='estimate[customer_id]']")?.dataset.customerType || "direct"
  }

  #consumeReplacement() {
    const item = this._pendingReplacement
    this._pendingReplacement = null
    return item?.isConnected ? item : null
  }

  #replaceCatalogueItem(item, colour, preservedVariantIds = new Set()) {
    this.#populateCatalogueItem(item, colour, true, preservedVariantIds)
  }

  #replaceCustomItem(item, selection) {
    this.#populateCustomItem(item, selection, true)
  }

  #populateCatalogueItem(item, colour, replacing = false, preservedVariantIds = new Set()) {
    const index = item.dataset.itemIndex

    this.#setField(item, "product_id", colour.product_id || "")
    this.#setField(item, "product_variant_id", colour.sizes?.[0]?.variant_id || "")
    this.#setField(item, "product_color_id", "")
    this.#setField(item, "product_colorway_id", colour.product_colorway_id || "")
    this.#setField(item, "customer_supplied", "0")
    this.#setField(item, "garment_type", "")
    this.#setField(item, "color_name", colour.color_name)
    this.#setField(item, "reference", colour.reference)
    this.#setField(item, "description", colour.description)
    this.#setField(item, "manual_price_reason", "")
    this.#setBooleanField(item, "unit_price_override", false)
    this.#setBooleanField(item, "complimentary", false)
    item.dataset.textileType = colour.pricing_class === "white" ? "white" : "colored"
    item.dataset.customerSupplied = "false"
    item.dataset.compatibilityRules = JSON.stringify(this.#compatibilityRules(colour.compatibility))

    if (colour.supplier_price_cents != null) {
      this.#setField(item, "supplier_price", (colour.supplier_price_cents / 100).toFixed(2))
    }
    this.#setUnitPrice(item, colour.unit_price_cents ?? 0)
    this.#applyCatalogueLayout(item)
    item.querySelector("[data-supplier-refresh]")?.classList.toggle("hidden", colour.supplier !== "imbretex")
    if (replacing) this.#replaceSizeRows(item, index, colour.sizes, preservedVariantIds)
    else this.#buildSizeRows(item, index, colour.sizes)
    this.#setImage(item, colour.image_url)
    this.#setSwatches(item, colour.hexes || [])
    this.#setProductLabel(item, colour.description)
    this.#syncQuantity(item, colour.quantity)
    this.#refreshItemSummary(item)
    item.querySelector("[data-erp--estimate-editor-target='itemForm']")?.classList.add("hidden")
  }

  #populateCustomItem(item, selection, replacing = false) {
    const {
      garment_type, garment_label, color_name, comment, sizes, quantity, description,
      unit_price_cents
    } = selection
    const index = item.dataset.itemIndex

    this.#setField(item, "product_id", "")
    this.#setField(item, "product_variant_id", "")
    this.#setField(item, "product_color_id", "")
    this.#setField(item, "product_colorway_id", "")
    this.#setField(item, "reference", "")
    this.#setField(item, "customer_supplied", "1")
    this.#setField(item, "garment_type", garment_type)
    this.#setField(item, "color_name", color_name)
    this.#setField(item, "description",
      description || [ garment_label, color_name, comment ].filter(Boolean).join(" — "))
    this.#setField(item, "manual_price_reason", "")
    this.#setBooleanField(item, "unit_price_override", false)
    this.#setBooleanField(item, "complimentary", false)
    item.dataset.textileType = /blanc|white/i.test(color_name || "") ? "white" : "colored"
    item.dataset.customerSupplied = "true"
    item.dataset.compatibilityRules = "{}"
    this.#setUnitPrice(item, unit_price_cents ?? 0)
    this.#applyCustomerSuppliedLayout(item)
    item.querySelector("[data-supplier-refresh]")?.classList.add("hidden")
    if (replacing) this.#replaceSizeRows(item, index, sizes)
    else this.#buildSizeRows(item, index, sizes)
    this.#setImage(item, null)
    this.#setSwatches(item, [])
    this.#setProductLabel(item, "")
    this.#syncQuantity(item, quantity)
    this.#refreshItemSummary(item)
    item.querySelector("[data-erp--estimate-editor-target='itemForm']")?.classList.add("hidden")
  }

  #destroyAllocationsForVariantIds(variantIds) {
    if (variantIds.size === 0) return

    this.markingTargets.forEach(marking => {
      marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']").forEach(row => {
        const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
        if (!variantIds.has(variantId)) return

        const destroyField = row.querySelector("input[name$='[_destroy]']")
        if (destroyField) destroyField.value = "1"
        row.style.display = "none"
      })
    })
  }

  #itemHasAllocations(item) {
    const variantIds = new Set(
      [...item.querySelectorAll("[data-size-row] input[type='hidden'][name$='[id]']")]
        .map(input => input.value)
        .filter(Boolean)
    )
    return [...this.element.querySelectorAll("[data-erp--marking-form-target='allocationRow']")].some(row => {
      const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
      const destroyed = row.querySelector("input[name$='[_destroy]']")?.value === "1"
      return variantIds.has(variantId) && !destroyed
    })
  }

  // A persisted row must be kept in the DOM and flagged for destruction; a brand new one can
  // simply go away.
  #discard(element, destroyTargetName) {
    const idField = element.querySelector("input[name*='[id]']")

    if (idField?.value) {
      const destroyField = element.querySelector(`[data-erp--estimate-editor-target='${destroyTargetName}']`)
      if (destroyField) destroyField.value = "1"
      element.classList.add("hidden")
      element.style.display = "none"
    } else {
      element.remove()
    }
  }

  // A catalogue size points at a product variant; a customer-supplied one carries its label only.
  #buildSizeRows(item, itemIndex, sizes) {
    const list = item.querySelector("[data-erp--estimate-editor-target='sizeList']")
    if (!list || !this.hasSizeTemplateTarget || !sizes?.length) return

    list.replaceChildren()
    sizes.forEach((size, position) => {
      const prefix = `estimate[items_attributes][${itemIndex}][item_variants_attributes][${position}]`
      list.insertAdjacentHTML("beforeend", this.sizeTemplateTarget.innerHTML.replace(/SIZE_PREFIX/g, prefix))

      const row = list.lastElementChild
      const label = size.size || size.size_label

      row.dataset.stock = size.stock ?? ""
      row.querySelector("[data-size-variant-id]").value = size.variant_id || ""
      row.querySelector("[data-size-canonical-id]").value = size.canonical_size_id || ""
      row.querySelector("[data-size-label-field]").value = size.variant_id ? "" : label
      row.querySelector("[data-size-label]").textContent = label
      row.querySelector("[data-size-quantity]").value = size.quantity
      row.querySelector("[data-size-quantity]").setAttribute("aria-label", t("size_quantity", { size: label }))
      // The autosave reconciles saved size rows by position, so it has to be the real one.
      row.querySelector("input[name*='[position]']").value = position
    })
  }

  #planReplacementMoves(item, colours) {
    const sourceRows = [...item.querySelectorAll("[data-size-row]")].filter(row => {
      return row.querySelector("input[type='hidden'][name$='[id]']")?.value &&
        row.querySelector("input[name$='[_destroy]']")?.value !== "1"
    })
    const usedRows = new Set()
    const moves = new Map()

    colours.forEach((colour, groupIndex) => {
      colour.sizes.forEach((size, sizeIndex) => {
        const sourceRow = this.#matchingSizeRow(sourceRows, usedRows, size)
        if (!sourceRow) return

        usedRows.add(sourceRow)
        if (groupIndex === 0) return

        const groupMoves = moves.get(groupIndex) || []
        groupMoves.push({
          sourceId: sourceRow.querySelector("input[type='hidden'][name$='[id]']").value,
          sizeIndex
        })
        moves.set(groupIndex, groupMoves)
      })
    })

    return moves
  }

  #matchingSizeRow(rows, usedRows, size) {
    const canonicalId = String(size.canonical_size_id || "")
    const label = String(size.size || size.size_label || "").trim().toLowerCase()

    return rows.find(row => {
      if (usedRows.has(row)) return false

      const existingCanonicalId = row.querySelector("[data-size-canonical-id]")?.value || ""
      const existingLabel = row.querySelector("[data-size-label]")?.textContent?.trim().toLowerCase()
      if (canonicalId && existingCanonicalId === canonicalId) return true
      return existingLabel === label
    })
  }

  #bindReplacementMoves(item, moves) {
    const rows = [...item.querySelectorAll("[data-size-row]")]
    moves.forEach(move => {
      const field = rows[move.sizeIndex]?.querySelector("[data-replacement-source-variant-id]")
      if (field) field.value = move.sourceId
    })
  }

  #replaceSizeRows(item, itemIndex, sizes, preservedVariantIds = new Set()) {
    const list = item.querySelector("[data-erp--estimate-editor-target='sizeList']")
    if (!list || !this.hasSizeTemplateTarget || !sizes?.length) return

    let nestedIndex = 0
    const activeRows = [...list.querySelectorAll("[data-size-row]")].filter(row => {
      row.querySelectorAll("input[name]").forEach(input => {
        const match = input.name.match(/\[item_variants_attributes\]\[(\d+)\]/)
        if (match) nestedIndex = Math.max(nestedIndex, parseInt(match[1]) + 1)
      })
      return row.querySelector("input[name$='[_destroy]']")?.value !== "1"
    })
    const usedRows = new Set()

    sizes.forEach((size, position) => {
      const label = size.size || size.size_label
      const canonicalId = String(size.canonical_size_id || "")
      const matchingRow = activeRows.find(row => {
        if (usedRows.has(row)) return false

        const existingCanonicalId = row.querySelector("[data-size-canonical-id]")?.value || ""
        const existingLabel = row.querySelector("[data-size-label]")?.textContent?.trim().toLowerCase()
        if (canonicalId && existingCanonicalId === canonicalId) return true
        return existingLabel === String(label || "").trim().toLowerCase()
      })

      if (matchingRow) {
        usedRows.add(matchingRow)
        matchingRow.dataset.stock = size.stock ?? ""
        matchingRow.querySelector("[data-size-variant-id]").value = size.variant_id || ""
        matchingRow.querySelector("[data-size-canonical-id]").value = canonicalId
        matchingRow.querySelector("[data-size-label-field]").value = size.variant_id ? "" : label
        matchingRow.querySelector("[data-size-label]").textContent = label
        matchingRow.querySelector("[data-size-quantity]").value = size.quantity
        matchingRow.querySelector("[data-size-quantity]").setAttribute("aria-label", t("size_quantity", { size: label }))
        matchingRow.querySelector("input[name*='[position]']").value = position
        matchingRow.querySelector("input[name$='[_destroy]']").value = "0"
        const replacementField = matchingRow.querySelector("[data-replacement-source-variant-id]")
        if (replacementField) replacementField.value = ""
        matchingRow.style.display = ""
        return
      }

      const prefix = `estimate[items_attributes][${itemIndex}][item_variants_attributes][${nestedIndex++}]`
      list.insertAdjacentHTML("beforeend", this.sizeTemplateTarget.innerHTML.replace(/SIZE_PREFIX/g, prefix))

      const row = list.lastElementChild
      row.dataset.stock = size.stock ?? ""
      row.querySelector("[data-size-variant-id]").value = size.variant_id || ""
      row.querySelector("[data-size-canonical-id]").value = canonicalId
      row.querySelector("[data-size-label-field]").value = size.variant_id ? "" : label
      row.querySelector("[data-size-label]").textContent = label
      row.querySelector("[data-size-quantity]").value = size.quantity
      row.querySelector("[data-size-quantity]").setAttribute("aria-label", t("size_quantity", { size: label }))
      row.querySelector("input[name*='[position]']").value = position
    })

    const removedVariantIds = new Set()
    activeRows.filter(row => !usedRows.has(row)).forEach(row => {
      const id = row.querySelector("input[type='hidden'][name$='[id]']")?.value
      if (id) {
        if (!preservedVariantIds.has(id)) removedVariantIds.add(id)
        row.querySelector("input[name$='[_destroy]']").value = "1"
        row.style.display = "none"
      } else {
        row.remove()
      }
    })
    this.#destroyAllocationsForVariantIds(removedVariantIds)
  }

  #sizeRowById(id) {
    return [...this.element.querySelectorAll("[data-size-row]")].find(row => {
      return row.querySelector("input[type='hidden'][name$='[id]']")?.value === String(id)
    })
  }

  #ensureAllocationItemSection(marking, item) {
    const itemId = item.dataset.itemId || item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
    const list = marking.querySelector("[data-marking-allocation-list]")
    if (!itemId || !list) return null

    let section = [...list.querySelectorAll("[data-allocation-item]")]
      .find(candidate => candidate.dataset.allocationItemId === itemId)
    if (section) return section

    list.insertAdjacentHTML(
      "beforeend",
      this.allocationItemTemplateTarget.innerHTML.replaceAll("ITEM_ID", itemId)
    )
    section = list.lastElementChild
    this.#fillAllocationItemSummary(section, item)
    list.querySelector("[data-allocation-empty]")?.remove()
    return section
  }

  #refreshMovedAllocationRow(row, item, sizeRow) {
    const size = sizeRow.querySelector("[data-size-label]")?.textContent?.trim() || ""
    const quantity = parseInt(sizeRow.querySelector("[data-size-quantity]")?.value) || 0
    const label = item.querySelector("[data-erp--estimate-editor-target='itemSummaryRef']")?.textContent?.trim()
      || item.querySelector("[data-erp--estimate-editor-target='itemSummaryDescription']")?.textContent?.trim()
      || ""
    const textileType = item.dataset.textileType === "white" ? "white" : "colored"
    const list = row.closest("[data-marking-allocation-list]")

    row.dataset.allocationType = textileType
    const typeField = row.querySelector("input[name$='[textile_type]']")
    if (typeField) typeField.value = textileType
    const quantityField = row.querySelector("input[name$='[quantity]']")
    if (quantityField) {
      quantityField.max = quantity
      quantityField.setAttribute("aria-label", t("allocation_label", { textile: label, size, max: quantity }))
    }
    const sizeLabel = row.querySelector("[data-allocation-size]")
    if (sizeLabel) sizeLabel.textContent = size
    const allocationLabel = row.querySelector("[data-allocation-label]")
    if (allocationLabel) allocationLabel.textContent = `${label} · ${size}`
    const available = row.querySelector("[data-allocation-available]")
    if (available) available.textContent = this.#availableLabel(list, quantity)
  }

  #applyCatalogueLayout(item) {
    item.querySelector("[data-item-mode='catalogue']")?.classList.remove("hidden")
    item.querySelector("[data-item-mode='customer']")?.classList.add("hidden")
    item.querySelectorAll("[data-cost-field]").forEach(field => field.classList.remove("hidden"))
    item.querySelector("[data-customer-supplied-badge]")?.classList.add("hidden")
  }

  #applyCustomerSuppliedLayout(item) {
    item.querySelector("[data-item-mode='catalogue']")?.classList.add("hidden")
    item.querySelector("[data-item-mode='customer']")?.classList.remove("hidden")
    item.querySelectorAll("[data-cost-field]").forEach(field => field.classList.add("hidden"))
    item.querySelector("[data-customer-supplied-badge]")?.classList.remove("hidden")
  }

  // The line quantity is always the sum of its sizes when a breakdown exists; a line without
  // one keeps the quantity it was given.
  #syncQuantity(item, fallbackQuantity) {
    const rows = [...item.querySelectorAll("[data-size-row]")].filter(row => {
      return row.querySelector("input[name*='[_destroy]']")?.value !== "1"
    })

    if (rows.length === 0) {
      if (fallbackQuantity) this.#setField(item, "quantity", fallbackQuantity)
      return
    }

    const total = rows.reduce((sum, row) => {
      return sum + (parseInt(row.querySelector("input[name*='[quantity]']")?.value) || 0)
    }, 0)

    const field = item.querySelector("[data-line-quantity]")
    if (field) {
      field.value = total
      field.readOnly = true
    }
  }

  // Keeps the textile card (_textile_card) in step with the line's fields
  #refreshItemSummary(item) {
    const value = (selector) => item.querySelector(selector)?.value || ""

    this.#setText(item, "itemSummaryRef", value("input[name*='[reference]']"))
    const colour = item.querySelector("[data-erp--estimate-editor-target='itemSummaryColor']")
    if (colour) colour.textContent = value("input[name*='[color_name]']") || colour.dataset.emptyLabel || ""
    this.#setText(item, "itemSummaryDescription", value("textarea[name*='[description]']") || t("new_line"))
    this.#refreshActionLabels(item)

    const unitPrice = parseFloat(value("input[name*='[unit_price]']"))
    const pricingUnavailable = item.dataset.pricingUnavailable === "true"
    const pricingStale = item.dataset.pricingStale === "true"
    this.#setText(item, "itemSummaryUnitPrice", this.#refreshPriceLine(item) ? this.#currency(unitPrice) : "—")

    const total = this.#discountedLineTotal(item)
    this.#setText(item, "itemSummaryTotal", pricingUnavailable ? t("pricing_unavailable") : (pricingStale ? t("reprice_required") : (total > 0 ? this.#currency(total) : "—")))

    // Size chips, flagged beyond the catalogue stock
    let overStock = 0
    const chips = item.querySelector("[data-erp--estimate-editor-target='itemSummarySizes']")
    if (chips) {
      chips.replaceChildren()
      item.querySelectorAll("[data-size-row]").forEach(row => {
        if (row.querySelector("input[name*='[_destroy]']")?.value === "1") return
        const input = row.querySelector("[data-size-quantity]")
        const quantity = parseInt(input?.value) || 0
        const over = row.dataset.stock !== undefined && row.dataset.stock !== "" && quantity > Number(row.dataset.stock)
        input?.toggleAttribute("data-over", over)
        if (quantity <= 0) return

        if (over) overStock += 1
        const chip = document.createElement("span")
        chip.className = chips.dataset.chipClasses || ""
        chip.toggleAttribute("data-over", over)
        chip.textContent = `${row.querySelector("[data-size-label]")?.textContent || ""} × ${quantity}`
        chips.appendChild(chip)
      })
    }
    const warning = item.querySelector("[data-item-stock-warning]")
    if (warning) {
      warning.classList.toggle("hidden", overStock === 0)
      warning.querySelector("span").textContent = t("over_stock", { count: overStock })
    }
  }

  // Accessible names of the card's actions follow the line (a cloned card starts as "Nouvelle ligne")
  #refreshActionLabels(item) {
    const templates = JSON.parse(item.dataset.actionLabels || "{}")
    const name = [item.querySelector("input[name*='[reference]']")?.value, item.querySelector("textarea[name*='[description]']")?.value]
      .filter(Boolean).join(" — ") || t("new_line")
    const label = (key) => (templates[key] || "").replace("__NAME__", name)

    item.querySelectorAll("[data-action-label]").forEach(button => button.setAttribute("aria-label", label(button.dataset.actionLabel)))
    item.querySelectorAll("[data-erp--menu-target='trigger'], [role='menu']").forEach(node => node.setAttribute("aria-label", label("more_actions")))
  }

  // "N pcs × price HT" under the card's total; returns whether the line has a known unit price
  #refreshPriceLine(item) {
    const known = this.#unitPriceKnown(item)
    item.querySelector("[data-item-unit-part]")?.classList.toggle("hidden", !known)
    item.querySelector("[data-item-no-textile-price]")?.classList.toggle("hidden", known || item.dataset.customerSupplied !== "true")
    const pieces = item.querySelector("[data-item-pieces]")
    if (pieces) pieces.textContent = t("pieces", { count: this.#lineQuantity(item) })
    return known
  }

  #unitPriceKnown(item) {
    if (item.dataset.pricingUnavailable === "true" || item.dataset.pricingStale === "true") return false

    const unitPrice = parseFloat(item.querySelector("input[name*='[unit_price]']")?.value)
    return Number.isFinite(unitPrice) && unitPrice > 0
  }

  // Copies the line's own fields (not its sizes' nested ones), its state and its sizes into a new line
  #copyItemFields(source, copy) {
    const targetFields = this.#ownItemFields(copy)
    this.#ownItemFields(source).forEach((from, name) => {
      const to = targetFields.get(name)
      if (!to) return
      if (from.type === "checkbox") to.checked = from.checked
      else to.value = from.value
      to.readOnly = from.readOnly
      to.dispatchEvent(new Event("change", { bubbles: true }))
    })
    this.#copyItemState(source, copy)
    this.#buildSizeRows(copy, copy.dataset.itemIndex, this.#activeSizes(source))
    this.#syncQuantity(copy, this.#lineQuantity(source))
    this.#refreshItemSummary(copy)
    copy.querySelector("[data-erp--estimate-editor-target='itemForm']")?.classList.add("hidden")
  }

  // The line's own fields by attribute name, without the ones a copy must not share
  #ownItemFields(item) {
    const OWN_FIELD = /^estimate\[items_attributes\]\[[^\]]+\]\[(\w+)\]$/
    const SKIPPED = new Set(["id", "_destroy", "position", "estimate_section_id", "section_editor_key", "quantity", "customer_assets", "duplicated_from_item_id"])
    // A Rails check box comes with a hidden "0" sibling: keep the box only
    const copiable = (field) => field.type !== "file" && !(field.type === "hidden" && field.nextElementSibling?.type === "checkbox")
    return new Map([...item.querySelectorAll("input[name], select[name], textarea[name]")]
      .filter(copiable)
      .map(field => [field.name.match(OWN_FIELD)?.[1], field])
      .filter(([name]) => name && !SKIPPED.has(name)))
  }

  #copyItemState(source, copy) {
    ;["textileType", "customerSupplied", "compatibilityRules", "pricingUnavailable", "pricingStale"].forEach(key => {
      if (source.dataset[key] !== undefined) copy.dataset[key] = source.dataset[key]
    })
    this.#copyItemDisplay(source, copy)
  }

  // Layout, supplier price refresh button, product label and thumbnail of the copied line
  #copyItemDisplay(source, copy) {
    if (source.dataset.customerSupplied === "true") this.#applyCustomerSuppliedLayout(copy)
    else this.#applyCatalogueLayout(copy)
    copy.querySelector("[data-supplier-refresh]")?.classList.toggle("hidden", source.querySelector("[data-supplier-refresh]")?.classList.contains("hidden") ?? true)
    this.#setProductLabel(copy, source.querySelector("[data-product-label]")?.textContent.trim() || "")
    this.#setImage(copy, source.querySelector("[data-variant-image] img")?.getAttribute("src") || null)
    this.#copySwatches(source, copy)
  }

  #copySwatches(source, copy) {
    const from = source.querySelector("[data-item-swatches]")
    const to = copy.querySelector("[data-item-swatches]")
    if (from && to) to.replaceChildren(...[...from.childNodes].map(node => node.cloneNode(true)))
  }

  // Size rows always carry these fields (_textile_card and the size template)
  #activeSizes(item) {
    return [...item.querySelectorAll("[data-size-row]")]
      .filter(row => row.querySelector("input[name$='[_destroy]']").value !== "1")
      .map(row => ({
        variant_id: row.querySelector("[data-size-variant-id]").value || null,
        canonical_size_id: row.querySelector("[data-size-canonical-id]").value || null,
        size: row.querySelector("[data-size-label]").textContent.trim(),
        stock: row.dataset.stock,
        quantity: parseInt(row.querySelector("[data-size-quantity]").value) || 0
      }))
  }

  // Mirrors EstimateItems::Computer#discounted_total_cents: percentage discounts are
  // rounded once on the complete line, never truncated once per piece.
  #discountedLineTotal(line) {
    return this.#discountedLineTotalCents(line) / 100
  }

  #discountedLineTotalCents(line) {
    if (line.dataset.pricingUnavailable === "true" || line.dataset.pricingStale === "true") return 0
    if (line.querySelector("input[type='checkbox'][name*='[complimentary]']")?.checked) return 0

    const quantity = this.#lineQuantity(line)
    const unitCents = Math.round((parseFloat(line.querySelector("input[name*='[unit_price]']")?.value) || 0) * 100)
    const discountType = line.querySelector("select[name*='[discount_type]']")?.value || "percentage"
    const discountPct = parseInt(line.querySelector("select[name*='[discount_percentage]']")?.value) || 0
    const discountCents = Math.round((parseFloat(line.querySelector("input[name*='[discount_amount]']")?.value) || 0) * 100)

    if (discountType === "fixed") return Math.max(0, unitCents - discountCents) * quantity
    if (discountPct > 0) return Math.round(unitCents * quantity * (100 - discountPct) / 100)
    return unitCents * quantity
  }

  // The line's own quantity input, never one of the nested size or attachment quantities.
  #lineQuantity(line) {
    return parseInt(line.querySelector("[data-line-quantity]")?.value) || 0
  }

  #vatRate(line) {
    return line.querySelector("select[name*='[vat_rate]']")?.value || "vat_20"
  }

  #setField(element, fieldName, value) {
    if (value === undefined || value === null) return

    const field = element.querySelector(`input[name*='[${fieldName}]'], select[name*='[${fieldName}]'], textarea[name*='[${fieldName}]']`)
    if (!field) return

    if (field instanceof HTMLSelectElement) this.#ensureOption(field, value)
    field.value = value
    field.dispatchEvent(new Event("input", { bubbles: true }))
    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // A select filled later (a marking's pricing choices) keeps the copied value until its options arrive
  #ensureOption(select, value) {
    const missing = value !== "" && ![ ...select.options ].some(option => option.value === value)
    if (missing) select.add(new Option(value, value))
  }

  #setText(element, targetName, text) {
    const node = element.querySelector(`[data-erp--estimate-editor-target='${targetName}']`)
    if (node) node.textContent = text
  }

  #setImage(item, imageUrl) {
    const container = item.querySelector("[data-variant-image]")
    if (!container) return

    container.replaceChildren()
    if (!imageUrl) {
      const icon = document.createElement("i")
      icon.className = "lni lni-shirt-1 text-2xl text-[#B9B09A]"
      icon.setAttribute("aria-hidden", "true")
      container.appendChild(icon)
      return
    }

    const img = document.createElement("img")
    img.addEventListener("error", () => this.#setImage(item, null), { once: true })
    img.src = imageUrl
    img.alt = ""
    img.loading = "lazy"
    img.className = "size-full bg-white object-contain"
    container.appendChild(img)
  }

  // Colour dots of the card (_color_swatches), from the hexes of the chosen colourway
  #setSwatches(item, hexes) {
    const container = item.querySelector("[data-item-swatches]")
    if (!container) return

    container.replaceChildren(...hexes.filter(hex => /^#[0-9a-f]{6}$/i.test(hex)).map(hex => {
      const dot = document.createElement("span")
      dot.className = "inline-block size-2.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.15)]"
      dot.style.backgroundColor = hex
      dot.setAttribute("aria-hidden", "true")
      return dot
    }))
  }

  #setUnitPrice(item, cents) {
    if (cents == null) return

    this.#setField(item, "unit_price", (cents / 100).toFixed(2))
  }

  #setBooleanField(item, fieldName, checked) {
    const checkbox = item.querySelector(`input[type='checkbox'][name*='[${fieldName}]']`)
    if (checkbox) {
      checkbox.checked = checked
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    const anchor = item.querySelector("input[name*='[unit_price]']")
    if (!anchor) return

    let hidden = item.querySelector(`input[type='hidden'][data-replacement-field='${fieldName}']`)
    if (!hidden) {
      hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = anchor.name.replace(/\[unit_price\]$/, `[${fieldName}]`)
      hidden.dataset.replacementField = fieldName
      item.prepend(hidden)
    }
    hidden.value = checked ? "1" : "0"
  }

  #setProductLabel(item, label) {
    const node = item.querySelector("[data-product-label]")
    if (node) node.textContent = label
  }

  #visible(elements) {
    return elements.filter(element => element.style.display !== "none")
  }

  #moveSibling(element, direction, selector) {
    if (this.#structuralLocked()) return false

    const siblings = this.#visible([...element.parentElement.children].filter(candidate => candidate.matches(selector)))
    const index = siblings.indexOf(element)
    const target = siblings[index + (direction === "up" ? -1 : 1)]
    if (!target) return false

    if (direction === "up") element.parentElement.insertBefore(element, target)
    else element.parentElement.insertBefore(target, element)
    return true
  }

  #structuralLocked() {
    return this.element.closest("form")?.dataset.saveState === "saving"
  }

  #deferStructuralAction(event, actionName, persist = true) {
    if (!this.#structuralLocked()) return false

    const snapshot = {
      target: event.target,
      currentTarget: event.currentTarget,
      params: { ...(event.params || {}) },
      detail: event.detail
    }
    this._queuedStructuralActions.push({ actionName, persist, snapshot })
    this.element.dispatchEvent(new CustomEvent("estimate-editor:structural-queued", { bubbles: true }))
    return true
  }

  #handleAutosaveRequestFinished(event) {
    if (!event.detail?.successful) return

    // The save rewrote the prices of the lines: show or hide the "× price HT" part of each card again
    this.#visible(this.itemTargets).forEach(item => this.#refreshPriceLine(item))
    this.#completeTextileMarkingFlow()
    if (this._queuedStructuralActions.length === 0) return

    const queued = this._queuedStructuralActions.splice(0)
    let shouldPersist = false
    queued.forEach(({ actionName, persist, snapshot }) => {
      const control = snapshot.currentTarget
      if (control instanceof Element && !control.isConnected) return

      this[actionName]({
        preventDefault() {},
        stopPropagation() {},
        ...snapshot
      })
      shouldPersist ||= persist
    })

    if (shouldPersist) this._autosaveForm?.requestSubmit()
  }

  #scheduleTextileMarkingFlow(items) {
    // Allocations reference persisted size rows. Save the selected textiles first, then
    // create the marking once the autosave response has populated those ids in the DOM.
    if (!this._autosaveForm?.matches("[data-controller~='erp--autosave']")) {
      this._pendingTextileMarkingItems = null
      return
    }

    const pendingItems = (this._pendingTextileMarkingItems || []).filter(item => item?.isConnected)
    const selectedItems = items.filter(item => item?.isConnected)
    this._pendingTextileMarkingItems = [ ...new Set([ ...pendingItems, ...selectedItems ]) ]
    this._triggerAutosave()
    this._autosaveForm?.requestSubmit()
  }

  #cancelPendingTextileMarkingItem(item) {
    if (!this._pendingTextileMarkingItems) return

    const remainingItems = this._pendingTextileMarkingItems.filter(candidate => candidate !== item && candidate.isConnected)
    this._pendingTextileMarkingItems = remainingItems.length > 0 ? remainingItems : null
  }

  #automaticMarkingsAllocatedToItem(item) {
    const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
    const variantIds = new Set(
      [...item.querySelectorAll("[data-size-row] input[type='hidden'][name$='[id]']")]
        .map(input => input.value)
        .filter(Boolean)
    )

    return this.markingTargets.filter(marking => {
      if (marking.dataset.automaticTextileMarking !== "true") return false

      const allocatedBySize = [...marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']")].some(row => {
        const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
        const destroyed = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value === "1"
        return variantIds.has(variantId) && !destroyed
      })
      const linkedWithoutSizes = itemId && [...marking.querySelectorAll("[data-automatic-marking-item-id]")].some(row => {
        const destroyed = row.querySelector("[data-erp--marking-form-target='linkDestroy']")?.value === "1"
        return row.dataset.automaticMarkingItemId === itemId && !destroyed
      })

      return allocatedBySize || linkedWithoutSizes
    })
  }

  #discardEmptyAutomaticMarkings(markings) {
    markings.forEach(marking => {
      const hasAllocation = [...marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']")].some(row => {
        return row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value !== "1"
      })
      const hasLegacyLink = [...marking.querySelectorAll("[data-automatic-marking-item-id]")].some(row => {
        return row.querySelector("[data-erp--marking-form-target='linkDestroy']")?.value !== "1"
      })
      if (!hasAllocation && !hasLegacyLink) this.#discard(marking, "markingDestroyField")
    })
    this._updateMarkingPositions()
  }

  #completeTextileMarkingFlow() {
    const items = (this._pendingTextileMarkingItems || []).filter(item => item.isConnected)
    if (items.length === 0 || !this.hasMarkingsListTarget) {
      this._pendingTextileMarkingItems = null
      return
    }

    const variantIds = items.flatMap(item => {
      return [...item.querySelectorAll("[data-size-row]")]
        .filter(row => row.querySelector("input[name$='[_destroy]']")?.value !== "1")
        .filter(row => (parseInt(row.querySelector("input[name$='[quantity]']")?.value) || 0) > 0)
        .map(row => row.querySelector("input[type='hidden'][name$='[id]']")?.value)
        .filter(Boolean)
    })
    const sizeLessItems = items.filter(item => {
      const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
      const activeSizeRows = [...item.querySelectorAll("[data-size-row]")].filter(row => {
        return row.querySelector("input[name$='[_destroy]']")?.value !== "1"
      })
      return itemId && activeSizeRows.length === 0 && this.#lineQuantity(item) > 0
    })

    this._pendingTextileMarkingItems = null
    if (variantIds.length === 0 && sizeLessItems.length === 0) return

    this.#closeOtherMarkings()
    const markings = []
    if (variantIds.length > 0) {
      const marking = this.#appendMarking()
      marking.dataset.automaticTextileMarking = "true"
      this.#syncMarkingAllocationRows(marking)
      this.#selectMarkingAllocations(marking, variantIds)
      markings.push(marking)
    }
    if (sizeLessItems.length > 0) {
      const marking = this.#appendMarking()
      marking.dataset.automaticTextileMarking = "true"
      this.#syncMarkingAllocationRows(marking)
      this.#appendAutomaticMarkingItemLinks(marking, sizeLessItems)
      markings.push(marking)
    }

    this._updateMarkingPositions()
    this._recalculateTotals()
    this.dispatch("changed")
    this._triggerAutosave()
    this.#openMarkingWizard(markings[markings.length - 1])
  }

  #appendAutomaticMarkingItemLinks(marking, items) {
    const positionField = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")
    if (!positionField?.name) return

    const markingPrefix = positionField.name.replace(/\[position\]$/, "")
    const container = document.createElement("div")
    container.hidden = true
    container.dataset.automaticMarkingLinks = ""

    items.forEach((item, index) => {
      const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
      if (!itemId) return

      const row = document.createElement("div")
      row.dataset.automaticMarkingItemId = itemId
      row.setAttribute("data-erp--marking-form-target", "linkRow")

      const appendHiddenField = (attribute, value, target) => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = `${markingPrefix}[marking_items_attributes][${index}][${attribute}]`
        input.value = value
        if (target) input.setAttribute("data-erp--marking-form-target", target)
        row.appendChild(input)
      }
      appendHiddenField("estimate_item_id", itemId)
      appendHiddenField("quantity", this.#lineQuantity(item), "linkQuantity")
      appendHiddenField("_destroy", "0", "linkDestroy")

      const label = document.createElement("span")
      label.dataset.linkLabel = ""
      label.textContent = item.querySelector("[data-erp--estimate-editor-target='itemSummaryRef']")?.textContent?.trim()
        || item.querySelector("[data-erp--estimate-editor-target='itemSummaryDescription']")?.textContent?.trim()
        || ""
      row.appendChild(label)
      container.appendChild(row)
    })

    marking.appendChild(container)
    this.#refreshAutomaticMarkingItemLinks(marking)
  }

  #syncAutomaticMarkingItemLinks(marking) {
    let removed = false
    const rows = [...marking.querySelectorAll("[data-automatic-marking-item-id]")]
    if (rows.length === 0) return removed

    const itemsById = new Map(this.#visible(this.itemTargets).map(item => {
      const itemId = item.querySelector(":scope > input[type='hidden'][name$='[id]']")?.value
      return [ itemId, item ]
    }).filter(([ itemId ]) => itemId))

    rows.forEach(row => {
      const item = itemsById.get(row.dataset.automaticMarkingItemId)
      const quantity = item ? this.#lineQuantity(item) : 0
      const quantityField = row.querySelector("[data-erp--marking-form-target='linkQuantity']")
      const destroyField = row.querySelector("[data-erp--marking-form-target='linkDestroy']")

      if (quantity > 0) {
        if (quantityField) quantityField.value = quantity
        return
      }

      if (destroyField?.value !== "1") removed = true
      if (destroyField) destroyField.value = "1"
    })

    this.#refreshAutomaticMarkingItemLinks(marking)
    return removed
  }

  #refreshAutomaticMarkingItemLinks(marking) {
    const controller = this.application.getControllerForElementAndIdentifier(marking, "erp--marking-form")
    if (controller) {
      controller.quantityChanged()
      return
    }

    requestAnimationFrame(() => this.application
      .getControllerForElementAndIdentifier(marking, "erp--marking-form")
      ?.quantityChanged())
  }

  #savedVariantIds(item) {
    return [ ...item.querySelectorAll("[data-size-row] input[type='hidden'][name$='[id]']") ].map(input => input.value).filter(Boolean)
  }

  // Every size of the given variant ids, or of all the textiles, with all its pieces
  #selectMarkingAllocations(marking, variantIds = null) {
    const selectedVariantIds = variantIds && new Set(variantIds)

    marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']:not(.hidden)").forEach(row => {
      const variantId = row.querySelector("input[name$='[estimate_item_variant_id]']")?.value
      if (selectedVariantIds && !selectedVariantIds.has(variantId)) return

      const destroy = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")
      const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
      if (!quantity || (parseInt(quantity.max) || 0) === 0) return

      if (destroy) destroy.value = "0"
      quantity.disabled = false
      quantity.value = quantity.max
      quantity.setAttribute("data-filled", "")
    })
  }

  // The wizard controller connects once the new card is in the DOM
  #openMarkingWizard(marking) {
    requestAnimationFrame(() => {
      if (!marking.isConnected) return

      const markingForm = this.application.getControllerForElementAndIdentifier(marking, "erp--marking-form")
      markingForm?.allocationsResynced()
      this.application.getControllerForElementAndIdentifier(marking, "erp--marking-wizard")?.open()
    })
  }

  #currency(amount) {
    return formatCurrency(amount)
  }

  // ── SortableJS ───────────────────────────────────────────────

  _initSortable() {
    if (this.hasSectionsContainerTarget) {
      Sortable.create(this.sectionsContainerTarget, {
        handle: ".section-drag-handle",
        draggable: "[data-section-index]",
        animation: 150,
        emptyInsertThreshold: 0,
        onMove: () => !this.#structuralLocked(),
        onEnd: () => {
          this._updateSectionPositions()
          this.dispatch("changed")
        }
      })
    }

    this.itemsListTargets.forEach(list => this._initSortableForList(list))
  }

  _initSortableForList(list) {
    if (list._sortable) return

    list._sortable = Sortable.create(list, {
      group: "items",
      handle: ".drag-handle",
      draggable: "[data-item-index]",
      animation: 150,
      fallbackOnBody: true,
      swapThreshold: 0.65,
      onMove: () => !this.#structuralLocked(),
      onEnd: (evt) => {
        this._updatePositions(evt.to)
        if (evt.from !== evt.to) this._updatePositions(evt.from)
        this._updateSectionIdForItem(evt.item, evt.to)
        this.dispatch("changed")
      }
    })
  }

  _updatePositions(container) {
    this.#visible([...container.children].filter(el => el.hasAttribute("data-item-index")))
      .forEach((item, index) => this.#setPosition(item, "positionField", index))
  }

  _updateMarkingPositions() {
    this.#visible([...this.markingsListTarget.children].filter(el => el.hasAttribute("data-marking-index")))
      .forEach((marking, index) => this.#setPosition(marking, "markingPositionField", index))
  }

  _updateSectionPositions() {
    this.#visible([...this.sectionsContainerTarget.children].filter(el => el.hasAttribute("data-section-index")))
      .forEach((section, index) => this.#setPosition(section, "sectionPositionField", index))
  }

  #setPosition(element, targetName, index) {
    const field = element.querySelector(`[data-erp--estimate-editor-target='${targetName}']`)
    if (!field) return

    field.value = index
    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _updateSectionIdForItem(itemEl, container) {
    const field = itemEl.querySelector("[data-erp--estimate-editor-target='sectionIdField']")
    if (field) field.value = container.dataset.sectionId || ""
    const editorKeyField = itemEl.querySelector("[data-erp--estimate-editor-target='sectionEditorKeyField']")
    if (editorKeyField) editorKeyField.value = container.dataset.sectionEditorKey || ""
  }

  _ungroupedItemsList() {
    return this.itemsListTargets.find(list => list.dataset.sectionId === "")
  }

  // The section the picker was opened from, unless it has since been removed from the DOM.
  _pickerContainer() {
    const container = this._pendingContainer
    this._pendingContainer = null

    return container?.isConnected ? container : this._ungroupedItemsList()
  }

  _triggerAutosave() {
    this.element.dispatchEvent(new CustomEvent("estimate-editor:autosave", { bubbles: true }))
  }
}
